/**
 * @module
 *
 * A lightweight assertions library based on https://github.com/elierotenberg/typed-assert.
 *
 * Use this for the purpose of lightweight type checking at runtime. It is not intended for general
 * purpose data validation from user input.
 *
 */
import { enumValues } from "./enum";

const expectedToBe = (type: string): string => `expected to be ${type}`;

export type WeakAssert = (input: unknown, message?: string, context?: Function) => void;

export type SubType<Input, Output> = Output extends Input ? Output : never;

export type Assert<Input = unknown, Output = Input> = (
  input: Input,
  message?: string,
  context?: Function,
) => asserts input is SubType<Input, Output>;

export type Check<Input = unknown, Output = Input> = (input: Input) => input is SubType<Input, Output>;

export const defaultAssert: WeakAssert = (condition, message, context = defaultAssert) => {
  if (!condition) {
    const err = new TypeError(message);
    Error.captureStackTrace(err, context);
    throw err;
  }
};

let baseAssert = defaultAssert;

export const assert: Assert<boolean, true> = (condition, message, context) =>
  baseAssert(condition, message, context ?? assert);

export function setBaseAssert(assert?: WeakAssert): void {
  if (assert) {
    baseAssert = assert;
  }
}

export const safeJsonParse = (json: string): unknown => JSON.parse(json) as unknown;

export function assertIsUnknown(_input: unknown): _input is unknown {
  return true;
}

export function assertIsNever(_input: never, message: string = "expected to be unreachable"): never {
  throw new TypeError(message);
}

export function assertIsNotNull<T>(
  input: null | T,
  message: string = "expected to be not null",
  context?: Function,
): asserts input is T {
  assert(input !== null, message, context ?? assertIsNotNull);
}

export function assertIsNotUndefined<T>(
  input: undefined | T,
  message: string = "expected to be not undefined",
  context?: Function,
): asserts input is T {
  assert(input !== undefined, message, context ?? assertIsNotUndefined);
}

export function assertIsNotNullOrUndefined<T>(
  input: null | undefined | T,
  message: string = "expected to be not null or undefined",
  context?: Function,
): asserts input is T {
  assert(input !== null && input !== undefined, message, context ?? assertIsNotNullOrUndefined);
}

export function assertIsNotVoid<T>(
  input: T,
  message: string = "expected to be neither null nor undefined",
  context?: Function,
): asserts input is Exclude<T, undefined | null | void> {
  assert(input !== null && input !== undefined, message, context ?? assertIsNotVoid);
}

export function assertIsExactly<Input, Output>(
  input: Input,
  value: Output,
  message = expectedToBe(`exactly ${value}`),
  context?: Function,
): asserts input is SubType<Input, Output> {
  assert((input as unknown) === (value as unknown), message, context ?? assertIsExactly);
}

export function assertIsBoolean(
  input: unknown,
  message: string = "expected to be a boolean",
  context?: Function,
): asserts input is boolean {
  assert(typeof input === "boolean", message, context ?? assertIsBoolean);
}

export function assertIsNumber(
  input: unknown,
  message: string = "expected to be a number",
  context?: Function,
): asserts input is number {
  assert(typeof input === "number", message, context ?? assertIsNumber);
}

export function assertIsString(
  input: unknown,
  message: string = "expected to be a string",
  context?: Function,
): asserts input is string {
  assert(typeof input === "string", message, context ?? assertIsString);
}

export function assertIsDate(
  input: unknown,
  message: string = "expected to be a Date",
  context?: Function,
): asserts input is Date {
  assert(input instanceof Date, message, context ?? assertIsDate);
}

export function assertIsRecord(
  input: unknown,
  message: string = "expected to be a record",
  context?: Function,
): asserts input is Record<string, unknown> {
  const ctx = context ?? assertIsRecord;
  assert(typeof input === "object", message, ctx);
  assertIsNotNull(input, message, ctx);
  for (const key of Object.keys(input as Record<string, unknown>)) {
    assertIsString(key, message, ctx);
  }
}

export function assertIsRecordWithKeys<K extends string>(
  input: unknown,
  keys: readonly K[],
  message = expectedToBe(`a record with keys ${keys.join(", ")}`),
): asserts input is {
  readonly [Key in K]: unknown;
} {
  assertIsRecord(input, message);
  for (const key of keys) {
    assertIsNotUndefined(input[key]);
  }
}

export function assertIsArray(
  input: unknown,
  message: string = "expected to be an array",
  context?: Function,
): asserts input is unknown[] {
  assert(Array.isArray(input), message, context ?? assertIsArray);
}

export function assertIsRecordOfType<T>(
  input: unknown,
  assertT: Assert<unknown, T>,
  message = "expected to be a record of given type",
  itemMessage = "expected to be of given type",
): asserts input is Record<string, T> {
  assertIsRecord(input, message);
  for (const item of Object.values(input)) {
    assertT(item, itemMessage);
  }
}

export function assertIsArrayOfType<T>(
  input: unknown,
  assertT: Assert<unknown, T>,
  message = "expected to be an array of given type",
  itemMessage = "expected to be of given type",
  context?: Function,
): asserts input is T[] {
  const ctx = context ?? assertIsArrayOfType;
  assertIsArray(input, message, ctx);
  for (const item of input) {
    assertT(item, itemMessage, ctx);
  }
}

export function assertIsOptionOfType<Input, Output>(
  input: Input | undefined,
  assertT: Assert<Input, Output>,
  message = "expected to be option of given type",
): asserts input is SubType<Input, Output | undefined> {
  if (input === undefined) {
    return;
  }
  assertT(input, message);
}

export function assertIsOneOf<Input, Output>(
  input: Input,
  values: readonly Output[],
  message: string = expectedToBe(`one of ${values.join(", ")}`),
  context?: Function,
): asserts input is SubType<Input, Output> {
  assert(values.includes(input as SubType<Input, Output>), message, context ?? assertIsOneOf);
}

export function assertIsNonEmptyArray<T>(
  input: unknown,
  message: string = "expected to be a non-empty array",
  context?: Function,
): asserts input is [T, ...T[]] {
  const ctx = context ?? assertIsNonEmptyArray;
  assertIsArray(input, message, ctx);
  assert(input.length > 0, message, ctx);
}

export function assertIsOneOfType<T>(
  input: unknown,
  assertT: Assert<unknown, T>[],
  message: string = expectedToBe(`one of type`),
  itemMessage?: string,
): asserts input is T {
  for (const assert of assertT) {
    try {
      (assert as WeakAssert)(input as T, itemMessage);
      return;
    } catch (_) {}
  }
  throw new TypeError(message);
}

export function assertIsInstanceOf<T>(
  input: unknown,
  constructor: new (...args: any[]) => T,
  message = "expected to be an instance of given constructor",
  context?: Function,
): asserts input is T {
  assert(input instanceof constructor, message, context ?? assertIsInstanceOf);
}

export function assertIsNotInstanceOf<B, T>(
  input: B,
  constructor: new (...args: any[]) => T,
  message = "expected not to be an instance of given constructor",
  context?: Function,
): asserts input is Exclude<B, T> {
  assert(!(input instanceof constructor), message, context ?? assertIsNotInstanceOf);
}

export function assertIsPromise(
  input: unknown,
  message = "expected to be a promise",
): asserts input is Promise<unknown> {
  assertIsInstanceOf(input, Promise, message);
}

export function assertIsStringOrNumber(
  input: unknown,
  message = "expected to be string or number",
): asserts input is string | number {
  assertIsOneOfType<string | number>(input, [assertIsString, assertIsNumber], message);
}

export function assertIsUndefined(input: unknown, message = "expected to be undefined"): asserts input is undefined {
  assertIsExactly(input, undefined, message);
}

export function check<Input, Output>(assertT: Assert<Input, Output>): Check<Input, Output> {
  return (input: Input): input is SubType<Input, Output> => {
    try {
      assertT(input);
      return true;
    } catch (_) {
      return false;
    }
  };
}

export function ensureDefined<T>(value: T | undefined | null): T {
  assertIsNotNullOrUndefined(value, undefined, ensureDefined);
  return value;
}

export function ensureIsInstanceOf<T>(value: any, constructor: new (...args: any[]) => T): T {
  assertIsInstanceOf(value, constructor, undefined, ensureIsInstanceOf);
  return value;
}

export function ensureIsArrayOfType<T>(value: unknown, assertT: Assert<unknown, T>): T[] {
  assertIsArrayOfType(value, assertT, undefined, undefined, ensureIsArrayOfType);
  return value;
}

export function ensureIsStringArray(value: unknown): string[] {
  return ensureIsArrayOfType(value, assertIsString);
}

export function assertIsEnumValue<O extends object>(
  value: unknown,
  obj: O,
  context?: Function,
): asserts value is O[keyof O] {
  assertIsOneOf(value, enumValues(obj), undefined, context ?? assertIsEnumValue);
}

export function ensureEnumValue<O extends object>(value: unknown, obj: O): O[keyof O] {
  assertIsEnumValue(value, obj, ensureEnumValue);
  return value;
}

export function ensureIsString(value: unknown): string {
  assertIsString(value, undefined, ensureIsString);
  return value;
}

export function ensureIsBoolean(value: unknown): boolean {
  assertIsBoolean(value, undefined, ensureIsBoolean);
  return value;
}

export function ensureIsNumber(value: unknown): number {
  assertIsNumber(value, undefined, ensureIsNumber);
  return value;
}
